import { Button, Slider } from '@hue/design-system';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { MAX_ZOOM, clamp, framedStyle, type ImageFrame } from './image-frame.js';

/// A imagem recortada como aparece na página: a moldura corta o que fica de fora.
export function FramedImage({
  src,
  alt,
  frame,
  className,
}: {
  src: string;
  alt: string;
  frame: ImageFrame;
  className: string;
}) {
  return (
    <div className={`overflow-hidden ${className}`}>
      <img src={src} alt={alt} className="size-full object-cover" style={framedStyle(frame)} />
    </div>
  );
}

/// Recortador: a moldura tem a proporção final, com linhas guia em terços, e só o que fica dentro
/// dela aparece. Arrastar (ou as setas) move; o controle de zoom, a roda do mouse, a pinça e as
/// teclas + e − aproximam. Nunca sobra área vazia dentro da moldura.
export function ImageFramer({
  src,
  label,
  aspect,
  frame,
  onChange,
}: {
  src: string;
  label: string;
  aspect: '2/1' | '4/3';
  frame: ImageFrame;
  onChange: (frame: ImageFrame) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const image = useRef<HTMLImageElement>(null);
  const [initial] = useState(frame);
  const [measured, setMeasured] = useState<{ x: boolean; y: boolean } | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ left: number; top: number; distance: number; frame: ImageFrame } | null>(
    null,
  );
  const latest = useRef({ frame, onChange });
  useEffect(() => {
    latest.current = { frame, onChange };
  });
  const hintId = useId();

  // Quanto a imagem passa da moldura em cada eixo, no zoom dado; só há o que mover onde sobra.
  function overflow(zoom: number) {
    const rect = box.current!.getBoundingClientRect();
    const { naturalWidth: width, naturalHeight: height } = image.current!;
    if (!width || !height || !rect.width) return { x: 0, y: 0 };
    const scale = Math.max(rect.width / width, rect.height / height) * (zoom / 100);
    return { x: width * scale - rect.width, y: height * scale - rect.height };
  }
  function zoomTo(zoom: number) {
    const current = latest.current.frame;
    const next = clamp(zoom, 100, MAX_ZOOM);
    if (next !== current.zoom) latest.current.onChange({ ...current, zoom: next });
  }

  // A roda do mouse aproxima sem rolar a página; o React só registra a roda como passiva.
  useEffect(() => {
    const element = box.current!;
    const wheel = (event: WheelEvent) => {
      event.preventDefault();
      zoomTo(latest.current.frame.zoom + (event.deltaY < 0 ? 10 : -10));
    };
    element.addEventListener('wheel', wheel, { passive: false });
    return () => element.removeEventListener('wheel', wheel);
  }, []);

  function spread() {
    const [a, b] = [...pointers.current.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  }
  function start(event: PointerEvent<HTMLDivElement>) {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    gesture.current = { left: event.clientX, top: event.clientY, distance: spread(), frame };
  }
  function move(event: PointerEvent<HTMLDivElement>) {
    if (!gesture.current || !pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const begin = gesture.current;
    if (pointers.current.size > 1) {
      // Pinça: a razão entre a distância atual dos dedos e a do início multiplica o zoom.
      if (begin.distance) zoomTo(begin.frame.zoom * (spread() / begin.distance));
      return;
    }
    const extra = overflow(frame.zoom);
    // Arrastar a imagem para a direita mostra mais do lado esquerdo: a porcentagem diminui.
    const x =
      extra.x > 0 ? clamp(begin.frame.x - ((event.clientX - begin.left) / extra.x) * 100) : frame.x;
    const y =
      extra.y > 0 ? clamp(begin.frame.y - ((event.clientY - begin.top) / extra.y) * 100) : frame.y;
    if (x !== frame.x || y !== frame.y) onChange({ ...frame, x, y });
  }
  function end(event: PointerEvent<HTMLDivElement>) {
    pointers.current.delete(event.pointerId);
    const rest = [...pointers.current.values()][0];
    // Sobrando um dedo depois da pinça, o arrasto recomeça dali, sem salto.
    gesture.current = rest ? { left: rest.x, top: rest.y, distance: 0, frame } : null;
  }
  function key(event: KeyboardEvent<HTMLDivElement>) {
    const step = event.shiftKey ? 1 : 5;
    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      zoomTo(frame.zoom + 10);
      return;
    }
    if (event.key === '-') {
      event.preventDefault();
      zoomTo(frame.zoom - 10);
      return;
    }
    const delta = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    }[event.key];
    if (!delta) return;
    event.preventDefault();
    onChange({ ...frame, x: clamp(frame.x + delta[0]!), y: clamp(frame.y + delta[1]!) });
  }

  const hint =
    frame.zoom > 100
      ? 'Arraste para escolher o que aparece. Use o zoom para aproximar ou afastar.'
      : !measured
        ? 'Arraste a imagem para escolher o que aparece. As setas do teclado também movem.'
        : measured.x
          ? 'A foto preenche a altura: arraste para os lados ou use o zoom para aproximar.'
          : measured.y
            ? 'A foto preenche a largura: arraste para cima ou para baixo ou use o zoom para aproximar.'
            : 'A foto já tem a proporção da moldura e aparece inteira. Use o zoom para aproximar.';
  const changed = frame.x !== initial.x || frame.y !== initial.y || frame.zoom !== initial.zoom;
  return (
    <div className="mt-4">
      <div
        ref={box}
        tabIndex={0}
        role="group"
        aria-roledescription="área de recorte"
        aria-label={label}
        aria-describedby={hintId}
        className={`relative w-full cursor-grab touch-none select-none overflow-hidden rounded-xl border border-surface-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 active:cursor-grabbing ${
          aspect === '2/1' ? 'aspect-[2/1]' : 'aspect-[4/3]'
        }`}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        onKeyDown={key}
      >
        <img
          ref={image}
          src={src}
          alt="Imagem selecionada"
          draggable={false}
          onLoad={() => {
            const extra = overflow(100);
            setMeasured({ x: extra.x > 1, y: extra.y > 1 });
          }}
          className="pointer-events-none size-full object-cover"
          style={framedStyle(frame)}
        />
        {/* Linhas guia em terços: ajudam a pôr o assunto num dos cruzamentos. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 grid grid-cols-3 grid-rows-3"
        >
          {Array.from({ length: 9 }, (_, index) => (
            <span
              key={index}
              className="border-white/50 shadow-[0_0_1px_rgb(0_0_0/.6)] [&:nth-child(-n+6)]:border-b [&:not(:nth-child(3n))]:border-r"
            />
          ))}
        </span>
      </div>
      <p id={hintId} className="mt-2 text-xs text-content-muted">
        {hint}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-3">
        <div className="flex min-w-48 flex-1 items-center gap-3">
          <span className="text-sm text-content-secondary">Zoom</span>
          <Slider
            label={`Zoom: ${label}`}
            value={frame.zoom}
            min={100}
            max={MAX_ZOOM}
            step={5}
            valueText={`${frame.zoom}%`}
            onChange={(zoom) => onChange({ ...frame, zoom })}
          />
          <span className="w-12 text-right text-sm tabular-nums text-content-muted">
            {frame.zoom}%
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={frame.x === 50 && frame.y === 50}
            onClick={() => onChange({ ...frame, x: 50, y: 50 })}
          >
            Centralizar
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!changed}
            onClick={() => onChange(initial)}
          >
            Desfazer ajustes
          </Button>
        </div>
      </div>
      <p className="sr-only" aria-live="polite">
        Horizontal {frame.x}%, vertical {frame.y}%, zoom {frame.zoom}%.
      </p>
    </div>
  );
}
